import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Switch,
  FormControlLabel,
  Fade,
  Skeleton,
  useTheme,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  Palette as PaletteIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import tipoDespesaService from '../../services/tipoDespesaService';

// =====================================================
// INTERFACES
// =====================================================

interface TipoDespesa {
  id: number;
  nome: string;
  descricao?: string;
  ativo: boolean;
  data_cadastro: string;
  ultima_atualizacao: string;
  id_usuario?: number;
}

interface TipoDespesaFormData {
  nome: string;
  descricao: string;
  ativo: boolean;
  id_usuario?: number;
}

interface DeleteDialogState {
  open: boolean;
  id: number | null;
  nome: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const TiposDespesa: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // =====================================================
  // ESTADOS
  // =====================================================
  const [tipos, setTipos] = useState<TipoDespesa[]>([]);
  const [filteredTipos, setFilteredTipos] = useState<TipoDespesa[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [formData, setFormData] = useState<TipoDespesaFormData>({
    nome: '',
    descricao: '',
    ativo: true
  });
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    open: false,
    id: null,
    nome: ''
  });
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});

  // =====================================================
  // EFEITOS
  // =====================================================
  useEffect(() => {
    carregarTipos();
  }, []);

  useEffect(() => {
    // Filtrar tipos baseado no termo de busca
    if (searchTerm.trim() === '') {
      setFilteredTipos(tipos);
    } else {
      const filtered = tipos.filter(tipo => 
        tipo.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (tipo.descricao && tipo.descricao.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredTipos(filtered);
    }
  }, [tipos, searchTerm]);

  // =====================================================
  // FUNÇÕES DE API
  // =====================================================
  const carregarTipos = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      
      if (!user?.id) {
        console.log('🔍 [DEBUG] Usuário não logado, não carregando tipos');
        setTipos([]);
        showSnackbar('Usuário não identificado!', 'warning');
        return;
      }
      
      const response = await tipoDespesaService.listarPorUsuario(user.id);
      
      let lista: TipoDespesa[] = [];
      if (Array.isArray(response)) {
        lista = response;
      } else if (response && Array.isArray((response as any).data)) {
        lista = (response as any).data;
      } else {
        console.error('⚠️ [DEBUG] Resposta inesperada ao carregar tipos de despesa:', response);
      }
      
      console.log('🔍 [DEBUG] Tipos carregados para usuário', user.id, ':', lista.length);
      
      setTipos(lista);
      if (lista.length > 0) {
        showSnackbar(`${lista.length} tipos de despesa carregados!`, 'success');
      }
    } catch (error: any) {
      console.error('Erro ao carregar tipos:', error);
      const mensagem = error?.message?.includes('401')
        ? 'Sessão expirada ou não autorizada. Faça login novamente.'
        : 'Erro ao carregar tipos de despesa. Tente novamente.';
      showSnackbar(mensagem, 'error');
      setTipos([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // =====================================================
  // FUNÇÕES DE MANIPULAÇÃO
  // =====================================================
  const showSnackbar = (message: string, severity: SnackbarState['severity']): void => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (): void => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.nome.trim()) {
      errors.nome = 'O campo Nome é obrigatório.';
    } else if (formData.nome.trim().length < 2) {
      errors.nome = 'O nome deve ter pelo menos 2 caracteres.';
    } else if (formData.nome.trim().length > 100) {
      errors.nome = 'O nome deve ter no máximo 100 caracteres.';
    }

    // Validação de nome duplicado removida - usuários podem ter tipos com nomes iguais

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNew = (): void => {
    setFormData({
      nome: '',
      descricao: '',
      ativo: true,
      id_usuario: user?.id || 0
    });
    setEditingId(null);
    setFormErrors({});
    setOpen(true);
  };

  const handleEdit = (tipo: TipoDespesa) => {
    setEditingId(tipo.id);
    setFormData({
      nome: tipo.nome,
      descricao: tipo.descricao || '',
      ativo: tipo.ativo,
      id_usuario: tipo.id_usuario || user?.id || 0
    });
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
    setEditingId(null);
    setFormErrors({});
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      
      if (editingId) {
        // Atualizar
        await tipoDespesaService.atualizar(editingId, formData);
        showSnackbar('Tipo de despesa atualizado com sucesso!', 'success');
      } else {
        // Criar - garantir que o id_usuario está definido
        const dadosParaCriar = {
          ...formData,
          id_usuario: user?.id || 0
        };
        
        console.log('🔍 [DEBUG] Criando tipo de despesa com dados:', dadosParaCriar);
        
        await tipoDespesaService.criar(dadosParaCriar);
        showSnackbar('Tipo de despesa criado com sucesso!', 'success');
      }
      
      // Recarregar lista
      await carregarTipos();
      handleClose();
      
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      showSnackbar(error.message || 'Erro ao salvar tipo de despesa.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (tipo: TipoDespesa): void => {
    setDeleteDialog({
      open: true,
      id: tipo.id,
      nome: tipo.nome
    });
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!deleteDialog.id) return;

    try {
      setLoading(true);
      await tipoDespesaService.excluir(deleteDialog.id);
      showSnackbar('Tipo de despesa excluído com sucesso!', 'success');
      await carregarTipos();
      handleDeleteCancel();
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      showSnackbar(error.message || 'Erro ao excluir tipo de despesa.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = (): void => {
    setDeleteDialog({ open: false, id: null, nome: '' });
  };

  // =====================================================
  // COMPONENTE DE LOADING SKELETON
  // =====================================================
  const LoadingSkeleton = () => (
    <TableBody>
      {[...Array(5)].map((_, index) => (
        <TableRow key={index}>
          <TableCell><Skeleton variant="rectangular" width={60} height={32} /></TableCell>
          <TableCell><Skeleton variant="text" width={200} /></TableCell>
          <TableCell><Skeleton variant="text" width={150} /></TableCell>
          <TableCell><Skeleton variant="rectangular" width={40} height={32} /></TableCell>
          <TableCell><Skeleton variant="rectangular" width={32} height={32} /></TableCell>
          <TableCell align="center">
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );

  // =====================================================
  // RENDER
  // =====================================================
  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Fade in timeout={300}>
        <Card sx={{ 
          mb: 3, 
          borderRadius: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton onClick={() => navigate(-1)} color="primary">
                  <ArrowBackIcon />
                </IconButton>
                <Box>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700, 
                    color: theme.palette.primary.main,
                    mb: 1,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    Tipos de Despesa
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Gerencie os tipos de despesa do sistema
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Buscar tipos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    minWidth: 250,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'background.paper'
                    }
                  }}
                />
                
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleNew}
                  sx={{ 
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    boxShadow: 3,
                    '&:hover': { 
                      boxShadow: 6,
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  Novo Tipo
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Fade>

      {/* Tabela */}
      <Fade in timeout={600}>
        <Card sx={{ 
          borderRadius: 3, 
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer sx={{ borderRadius: 3, backgroundColor: 'transparent' }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: theme.palette.primary.main }}>Nome</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: theme.palette.primary.main }}>Descrição</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: theme.palette.primary.main }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: theme.palette.primary.main }}>Data Cadastro</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                
                {loading ? (
                  <LoadingSkeleton />
                ) : (
                  <TableBody>
                    {filteredTipos.map((tipo, index) => (
                      <Fade in timeout={300 + (index * 100)} key={tipo.id}>
                        <TableRow key={tipo.id} hover sx={{ '& td': { py: 1 }, backgroundColor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.05)' : 'transparent' }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {tipo.nome}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {tipo.descricao || 'Sem descrição'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tipo.ativo ? 'Ativo' : 'Inativo'}
                              color={tipo.ativo ? 'success' : 'default'}
                              size="small"
                              sx={{ 
                                borderRadius: 2,
                                color: 'white',
                                '& .MuiChip-label': {
                                  color: 'white'
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(tipo.data_cadastro).toLocaleDateString('pt-BR')}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(tipo)}
                                  sx={{ 
                                    color: theme.palette.primary.main,
                                    '&:hover': { 
                                      backgroundColor: `${theme.palette.primary.main}15`,
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease-in-out'
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Excluir">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(tipo)}
                                  sx={{ 
                                    color: theme.palette.error.main,
                                    '&:hover': { 
                                      backgroundColor: `${theme.palette.error.main}15`,
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease-in-out'
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </Fade>
                    ))}
                    {filteredTipos.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <BusinessIcon sx={{ fontSize: 48, color: theme.palette.grey[400] }} />
                            <Typography variant="h6" color="text.secondary">
                              {searchTerm ? 'Nenhum resultado encontrado' : 'Nenhum tipo de despesa cadastrado'}
                            </Typography>
                            {searchTerm && (
                              <Typography variant="body2" color="text.secondary">
                                Tente ajustar os termos da busca
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Fade>

      {/* Dialog de Formulário */}
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            boxShadow: 10
          }
        }}
        TransitionComponent={Fade}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
            {editingId ? 'Editar Tipo de Despesa' : 'Novo Tipo de Despesa'}
          </Typography>
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Nome do Tipo"
              fullWidth
              variant="outlined"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              required
              disabled={saving}
              error={!!formErrors.nome}
              helperText={formErrors.nome}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
            
            <TextField
              margin="dense"
              label="Descrição"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              disabled={saving}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.ativo}
                  onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                  disabled={saving}
                />
              }
              label="Ativo"
            />
          </DialogContent>
          
          <DialogActions sx={{ p: 3, pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Button 
              onClick={handleClose}
              disabled={saving}
              sx={{ borderRadius: 2 }}
            >
              Cancelar
            </Button>
            <Button 
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ 
                minWidth: 120,
                borderRadius: 2,
                boxShadow: 3,
                '&:hover': { boxShadow: 6 }
              }}
            >
              {saving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                editingId ? 'Atualizar' : 'Criar'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            boxShadow: 10
          }
        }}
      >
        <DialogTitle sx={{ color: theme.palette.error.main, fontWeight: 700 }}>
          Confirmar Exclusão
        </DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o tipo de despesa <strong>"{deleteDialog.nome}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleDeleteCancel}
            sx={{ borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={loading}
            sx={{ 
              borderRadius: 2,
              boxShadow: 3,
              '&:hover': { boxShadow: 6 }
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: 4
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TiposDespesa;
